// WP-2.6 (AM-1..4, M24, S-06 `/chat`): "identical pipeline as WhatsApp" (AI-1), rendered as
// a chat transcript instead of WhatsApp messages. Two-pane on desktop (chat left, "created
// records" side list right per spec — derived from this session's own turns, not a second
// data source), single pane on mobile (the right pane just hides below `lg`, matching
// Sidebar's own breakpoint convention). Push-to-talk mic is a functional-parity build: it
// records, sends, and honors AI-3's low-confidence review pause — the spec's animated
// waveform is a deliberate visual scope trim (a simple recording/elapsed indicator instead),
// documented rather than silently dropped.
import type { CaptureItemResponse, CaptureMode } from '@pfos/shared';
import { formatMoney, type CurrencyCode } from '@pfos/engine';
import { Mic, Send, Square } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ghostButtonInlineClass,
  inputClass,
  primaryButtonInlineClass,
} from '../components/FormKit';
import { Toast } from '../components/Toast';
import { useRecordInboxItem, useRejectInboxItem } from '../lib/inbox';
import { useChatCapture, useChatVoiceCapture } from '../lib/chat';

interface ChatTurn {
  id: string;
  utterance: string;
  reply: string | null;
  item: CaptureItemResponse | null;
  mode: CaptureMode | null;
  error: string | null;
}

function proposalSummary(
  item: CaptureItemResponse,
): { amount: string; category?: string; account?: string; party?: string } | null {
  const ex = item.extraction;
  const amountMinor = ex?.amountMinor ?? null;
  const currency = ex?.currency ?? null;
  if (amountMinor === null || currency === null) return null;
  return {
    amount: formatMoney({ currency: currency as CurrencyCode, minorUnits: amountMinor }),
    ...(ex?.category != null ? { category: ex.category.label } : {}),
    ...(ex?.account != null ? { account: ex.account.label } : {}),
    ...(ex?.party != null ? { party: ex.party.label } : {}),
  };
}

/** Mode B/C's proposal card — [✓ Confirm] [✏ Adjust] [✕ Reject] map onto the SAME
 * record/reject routes S-08's Inbox item page uses (IN-4, LG-2's reject-is-a-delete). */
function ChatProposalCard({
  reply,
  item,
}: {
  reply: string;
  item: CaptureItemResponse;
}): React.JSX.Element {
  const [current, setCurrent] = useState(item);
  const [rejected, setRejected] = useState(false);
  const record = useRecordInboxItem(item.id);
  const reject = useRejectInboxItem(item.id);
  const summary = proposalSummary(current);

  return (
    <div className="max-w-[85%] rounded-[var(--radius-card)] border border-border bg-surface px-3 py-2 text-sm text-ink">
      <p>{reply}</p>
      {summary !== null && (
        <p className="mt-1 text-xs text-muted">
          {summary.amount}
          {summary.category !== undefined && <> · {summary.category}</>}
          {summary.account !== undefined && <> · {summary.account}</>}
          {summary.party !== undefined && <> · {summary.party}</>}
        </p>
      )}

      {rejected ? (
        <p className="mt-2 text-xs text-muted">Skipped.</p>
      ) : (
        <>
          {current.status === 'needs_one_tap' && (
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <button
                type="button"
                disabled={record.isPending}
                onClick={() => {
                  record.mutate(undefined, {
                    onSuccess: (res) => {
                      setCurrent((prev) => ({
                        ...prev,
                        status: 'auto_recorded',
                        resultEntityType: 'transaction',
                        resultEntityId: res.transactionId,
                      }));
                    },
                  });
                }}
                className={primaryButtonInlineClass}
              >
                ✓ Confirm
              </button>
              <Link to={`/inbox/${current.id}`} className={ghostButtonInlineClass}>
                ✏ Adjust
              </Link>
              <button
                type="button"
                disabled={reject.isPending}
                onClick={() => {
                  reject.mutate(undefined, {
                    onSuccess: () => {
                      setRejected(true);
                    },
                  });
                }}
                className="text-sm font-medium text-danger hover:underline"
              >
                ✕ Reject
              </button>
            </div>
          )}
          {(current.status === 'needs_question' || current.status === 'unparsed_failed') && (
            <Link
              to={`/inbox/${current.id}`}
              className="mt-2 inline-block text-sm text-primary hover:underline"
            >
              Review in Inbox →
            </Link>
          )}
          {current.status === 'auto_recorded' && current.resultEntityId !== null && (
            <Link
              to={`/tx/${current.resultEntityId}`}
              className="mt-2 inline-block text-sm text-primary hover:underline"
            >
              View record →
            </Link>
          )}
        </>
      )}
    </div>
  );
}

function ChatTurnView({ turn }: { turn: ChatTurn }): React.JSX.Element {
  return (
    <div className="mb-4 flex flex-col items-end gap-2">
      <p className="max-w-[85%] rounded-[var(--radius-card)] bg-primary px-3 py-2 text-sm text-primary-ink">
        {turn.utterance}
      </p>
      <div className="w-full flex flex-col items-start">
        {turn.error !== null && (
          <p className="max-w-[85%] rounded-[var(--radius-card)] border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">
            {turn.error}
          </p>
        )}
        {turn.reply !== null && turn.item !== null && (
          <ChatProposalCard reply={turn.reply} item={turn.item} />
        )}
        {turn.reply !== null && turn.item === null && (
          <p className="max-w-[85%] rounded-[var(--radius-card)] border border-border bg-surface px-3 py-2 text-sm text-ink">
            {turn.reply}
          </p>
        )}
        {turn.reply === null && turn.error === null && (
          <p className="text-xs text-muted">Thinking…</p>
        )}
      </div>
    </div>
  );
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => {
      reject(new Error('Could not read the recording'));
    };
    reader.readAsDataURL(blob);
  });
}

export function ChatPage(): React.JSX.Element {
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [draft, setDraft] = useState('');
  const [lastAutoRecorded, setLastAutoRecorded] = useState<{
    turnId: string;
    reply: string;
  } | null>(null);
  const [review, setReview] = useState<{ mediaId: string; transcript: string } | null>(null);
  const [recording, setRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);

  const chatCapture = useChatCapture();
  const voiceCapture = useChatVoiceCapture();
  const listRef = useRef<HTMLDivElement | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  // lib.dom.d.ts types `navigator.mediaDevices` as always-present, but it's genuinely absent
  // in non-secure contexts and in jsdom (this page's own test environment) — a real runtime
  // feature check, not a redundant one, despite what the types claim.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const micSupported = typeof navigator.mediaDevices?.getUserMedia === 'function';

  useEffect(() => {
    const el = listRef.current;
    if (el !== null && typeof el.scrollTo === 'function') el.scrollTo({ top: el.scrollHeight });
  }, [turns, review]);

  useEffect(
    () => () => {
      if (timerRef.current !== null) window.clearInterval(timerRef.current);
    },
    [],
  );

  async function submitUtterance(
    text: string,
    extra?: { mediaId: string; lowConfidence: boolean },
  ): Promise<void> {
    const localId = crypto.randomUUID();
    // AM-4: any next interaction clears the prior Mode-A undo eligibility — mirrored here so
    // the toast disappears the moment a new message is sent, exactly when the backend clears it.
    setLastAutoRecorded(null);
    setTurns((prev) => [
      ...prev,
      { id: localId, utterance: text, reply: null, item: null, mode: null, error: null },
    ]);
    try {
      const res = await chatCapture.mutateAsync({ text, ...(extra ?? {}) });
      setTurns((prev) =>
        prev.map((t) =>
          t.id === localId ? { ...t, reply: res.reply, item: res.item, mode: res.mode } : t,
        ),
      );
      if (res.mode === 'A') setLastAutoRecorded({ turnId: localId, reply: res.reply });
    } catch {
      setTurns((prev) =>
        prev.map((t) =>
          t.id === localId ? { ...t, error: "Couldn't reach the assistant — try again." } : t,
        ),
      );
    }
  }

  function handleSend(): void {
    const text = draft.trim();
    if (text === '') return;
    setDraft('');
    void submitUtterance(text);
  }

  function handleUndo(): void {
    setLastAutoRecorded(null);
    void submitUtterance('undo');
  }

  async function startRecording(): Promise<void> {
    if (!micSupported || recording) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';
      const recorder = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setRecording(true);
      setRecordingSeconds(0);
      timerRef.current = window.setInterval(() => {
        setRecordingSeconds((s) => s + 1);
      }, 1000);
    } catch {
      /* permission denied or no device — the mic button simply does nothing further */
    }
  }

  async function stopRecordingAndSend(): Promise<void> {
    const recorder = mediaRecorderRef.current;
    if (recorder === null) return;
    setRecording(false);
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    const blob = await new Promise<Blob>((resolve) => {
      recorder.onstop = () => {
        recorder.stream.getTracks().forEach((t) => {
          t.stop();
        });
        resolve(new Blob(chunksRef.current, { type: recorder.mimeType }));
      };
      recorder.stop();
    });
    mediaRecorderRef.current = null;
    if (blob.size === 0) return;

    setLastAutoRecorded(null);
    const base64 = await blobToBase64(blob);
    try {
      const res = await voiceCapture.mutateAsync({ mimeType: blob.type, base64 });
      if (res.status === 'needs_review') {
        setReview({ mediaId: res.mediaId, transcript: res.transcript });
        return;
      }
      if (res.status === 'failed') {
        setTurns((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            utterance: '🎙 (voice note)',
            reply: null,
            item: null,
            mode: null,
            error: `Couldn't transcribe that: ${res.reason}`,
          },
        ]);
        return;
      }
      const localId = crypto.randomUUID();
      setTurns((prev) => [
        ...prev,
        {
          id: localId,
          utterance: res.transcript,
          reply: res.reply,
          item: res.item,
          mode: res.mode,
          error: null,
        },
      ]);
      if (res.mode === 'A') setLastAutoRecorded({ turnId: localId, reply: res.reply });
    } catch {
      setTurns((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          utterance: '🎙 (voice note)',
          reply: null,
          item: null,
          mode: null,
          error: "Couldn't reach the assistant — try again.",
        },
      ]);
    }
  }

  function sendReview(): void {
    if (review === null) return;
    const { mediaId, transcript } = review;
    setReview(null);
    void submitUtterance(transcript, { mediaId, lowConfidence: true });
  }

  const createdRecords = turns.filter(
    (t) => t.item !== null && t.item.status === 'auto_recorded' && t.item.resultEntityId !== null,
  );

  return (
    <div>
      <h1 className="mb-4 text-xl font-semibold text-ink">Assistant</h1>
      <div className="flex gap-4">
        <div className="flex min-h-[60vh] min-w-0 flex-1 flex-col rounded-[var(--radius-card)] border border-border bg-bg">
          <div ref={listRef} className="flex-1 space-y-1 overflow-y-auto p-4">
            {turns.length === 0 && (
              <p className="text-sm text-muted">
                Tell me what happened — "chai 250 cash", "paid Ali 5000 for lunch" — text or hold{' '}
                {micSupported ? 'the mic' : 'not available in this browser'} to talk.
              </p>
            )}
            {turns.map((turn) => (
              <ChatTurnView key={turn.id} turn={turn} />
            ))}
          </div>

          {review !== null && (
            <div className="border-t border-border bg-warn/10 p-3">
              <p className="mb-1 text-xs font-medium text-ink">
                AI-3: low-confidence transcript — check it before sending
              </p>
              <textarea
                className={inputClass}
                rows={2}
                value={review.transcript}
                onChange={(e) => {
                  setReview({ ...review, transcript: e.target.value });
                }}
              />
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  className={primaryButtonInlineClass}
                  disabled={review.transcript.trim() === ''}
                  onClick={sendReview}
                >
                  Send
                </button>
                <button
                  type="button"
                  className={ghostButtonInlineClass}
                  onClick={() => {
                    setReview(null);
                  }}
                >
                  Discard
                </button>
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 border-t border-border p-3">
            <input
              className={inputClass}
              placeholder="Type a message…"
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSend();
              }}
              aria-label="Message"
            />
            {micSupported && (
              <button
                type="button"
                aria-label={recording ? 'Release to send' : 'Hold to record'}
                onPointerDown={() => {
                  void startRecording();
                }}
                onPointerUp={() => {
                  void stopRecordingAndSend();
                }}
                onPointerLeave={() => {
                  if (recording) void stopRecordingAndSend();
                }}
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
                  recording ? 'bg-danger text-white' : 'border border-border text-ink hover:bg-bg'
                }`}
              >
                {recording ? <Square size={14} /> : <Mic size={16} />}
              </button>
            )}
            <button
              type="button"
              aria-label="Send"
              onClick={handleSend}
              disabled={draft.trim() === ''}
              className={primaryButtonInlineClass}
            >
              <Send size={16} />
            </button>
          </div>
          {recording && (
            <p className="px-3 pb-2 text-xs text-danger">
              ● Recording {recordingSeconds}s — release to send
            </p>
          )}
        </div>

        <div className="hidden w-72 shrink-0 flex-col rounded-[var(--radius-card)] border border-border bg-surface p-3 lg:flex">
          <h2 className="mb-2 text-sm font-medium text-ink">Created this session</h2>
          {createdRecords.length === 0 ? (
            <p className="text-xs text-muted">Nothing recorded yet.</p>
          ) : (
            <ul className="space-y-2">
              {createdRecords.map((turn) => {
                const item = turn.item;
                if (item === null) return null;
                const summary = proposalSummary(item);
                return (
                  <li key={turn.id}>
                    <Link
                      to={`/tx/${item.resultEntityId ?? ''}`}
                      className="block rounded-[var(--radius-control)] border border-border px-3 py-2 hover:bg-bg"
                    >
                      <span className="block text-sm text-ink">{summary?.amount}</span>
                      <span className="block text-xs text-muted">
                        {summary?.category}
                        {summary?.account !== undefined && <> · {summary.account}</>}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {lastAutoRecorded !== null && (
        <Toast
          message="Recorded"
          actionLabel="Undo"
          onAction={handleUndo}
          onDismiss={() => {
            setLastAutoRecorded(null);
          }}
        />
      )}
    </div>
  );
}
